/**
 * SYSTEM_ADMIN — ai-gateway /health/config GUI — Cycle 20.
 *
 * env summary 4 섹션 (safe / secret(mask) / partial / validation) 을 시각화.
 * INTERNAL_API_TOKEN 은 backend proxy 에서만 사용 — 브라우저에는 secret 자체가 도달 0.
 *
 * Cycle 69: i18n 적용 (cycle 67 false negative 5건 중 본 page). 11 hardcoded → sysadmin-i18n.ts.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { getAiGatewayConfig, type AiGatewayConfig } from '../../api/systemAdmin';
// Cycle 43 — lang-aware date locale, Cycle 69 — sysadmin-i18n
import { formatDateTime } from '../../lib/base-i18n';
import { tSysadmin, getSysadminLang, subscribeSysadminLang } from '../../lib/sysadmin-i18n';
import '../AdminConsolePage/AdminConsolePage.css';

export function AiGatewayConfigPage() {
  useSyncExternalStore(subscribeSysadminLang, getSysadminLang, getSysadminLang);
  const user = useAuthStore((s) => s.user);
  const [config, setConfig] = useState<AiGatewayConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function reload() {
    setLoading(true);
    setError(null);
    try {
      setConfig(await getAiGatewayConfig());
    } catch (e: any) {
      const data = e?.response?.data;
      const detail = data?.upstream_status ? tSysadmin('sysadmin.gateway.err_upstream', { status: data.upstream_status }) : '';
      setError(`${data?.message ?? tSysadmin('sysadmin.gateway.err_load')}${detail}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void reload(); }, []);

  if (!user || user.role !== 'SYSTEM_ADMIN') {
    return <div className="admin-console admin-console--denied"><h2>{tSysadmin('sysadmin.access_denied')}</h2></div>;
  }

  return (
    <div className="admin-console">
      <aside className="admin-console__sidebar">
        <Link to="/system-admin" className="admin-console__btn" style={{ width: '100%', textDecoration: 'none', textAlign: 'center', display: 'block' }}>
          {tSysadmin('sysadmin.gateway.back_to_tenants')}
        </Link>
      </aside>
      <main className="admin-console__content">
        <h2 className="admin-console__heading">{tSysadmin('sysadmin.gateway.heading')}</h2>
        <p className="admin-console__subheading">
          {tSysadmin('sysadmin.gateway.subtitle')}
        </p>

        {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
        {loading && <p>{tSysadmin('sysadmin.loading')}</p>}

        {!loading && (
          <button type="button" className="admin-console__btn" onClick={() => void reload()} style={{ marginBottom: '1rem' }}>
            {tSysadmin('sysadmin.gateway.btn_refresh')}
          </button>
        )}

        {config && (
          <>
            <section style={{ marginBottom: '1.5rem' }} aria-label="validation 결과">
              <h3>{tSysadmin('sysadmin.gateway.validation_section')}</h3>
              <div
                style={{
                  padding: '0.75rem',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border, #e5e7eb)',
                  background: config.validation.passed
                    ? 'rgba(21, 128, 61, 0.08)'
                    : 'rgba(185, 28, 28, 0.08)',
                  color: config.validation.passed
                    ? 'var(--color-success, #15803d)'
                    : 'var(--color-danger, #b91c1c)',
                  fontWeight: 600,
                }}
                data-testid="validation-banner"
              >
                {config.validation.production
                  ? (config.validation.passed ? tSysadmin('sysadmin.gateway.validation.prod_passed') : tSysadmin('sysadmin.gateway.validation.prod_failed'))
                  : tSysadmin('sysadmin.gateway.validation.non_prod')}
              </div>
              {config.validation.issues.length > 0 && (
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  {config.validation.issues.map((i, idx) => (
                    <li key={idx} style={{ fontSize: '0.8125rem' }}>{i}</li>
                  ))}
                </ul>
              )}
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary, #6b7280)', marginTop: '0.5rem' }}>
                {tSysadmin('sysadmin.gateway.response_time', { time: formatDateTime(config.ts) })}
              </p>
            </section>

            <section style={{ marginBottom: '1.5rem' }} aria-label="safe env">
              <h3>{tSysadmin('sysadmin.gateway.safe_section')}</h3>
              <table className="admin-console__table">
                <thead><tr><th>key</th><th>value</th></tr></thead>
                <tbody>
                  {Object.entries(config.safe).map(([k, v]) => (
                    <tr key={k}>
                      <td><code style={{ fontSize: '0.8125rem' }}>{k}</code></td>
                      <td>
                        {v === null || v === undefined
                          ? <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>(unset)</span>
                          : <code style={{ fontSize: '0.8125rem' }}>{v}</code>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section style={{ marginBottom: '1.5rem' }} aria-label="secret env">
              <h3>{tSysadmin('sysadmin.gateway.secret_section')}</h3>
              <table className="admin-console__table">
                <thead><tr><th>key</th><th>set</th><th>length</th><th>prefix</th></tr></thead>
                <tbody>
                  {Object.entries(config.secret).map(([k, v]) => (
                    <tr key={k}>
                      <td><code style={{ fontSize: '0.8125rem' }}>{k}</code></td>
                      <td>
                        {v.set
                          ? <span style={{ color: 'var(--color-success, #15803d)' }}>✓</span>
                          : <span style={{ color: 'var(--color-danger, #b91c1c)' }}>✗</span>}
                      </td>
                      <td>{v.len ?? '—'}</td>
                      <td>{v.prefix4 ? <code style={{ fontSize: '0.8125rem' }}>{v.prefix4}…</code> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            {Object.keys(config.partial).length > 0 && (
              <section aria-label="partial env">
                <h3>{tSysadmin('sysadmin.gateway.partial_section')}</h3>
                <table className="admin-console__table">
                  <thead><tr><th>key</th><th>value</th></tr></thead>
                  <tbody>
                    {Object.entries(config.partial).map(([k, v]) => (
                      <tr key={k}>
                        <td><code style={{ fontSize: '0.8125rem' }}>{k}</code></td>
                        <td>
                          {v ? <code style={{ fontSize: '0.8125rem' }}>{v}</code> : <span style={{ color: 'var(--color-text-secondary, #6b7280)' }}>(unset)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
